import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../../context/AuthContext.js';
import dashboardService from '../../../services/DashboardService.js';
import { locationHubService } from '../../../services/locationHubService.js';
import {
  dashboardCacheKey,
  readDashboardCache,
  writeDashboardCache,
} from '../../../utils/dashboardCache.js';
import { buildDateParams, buildAbsoluteDateParams, DEFAULT_DATE_RANGE } from './DateRangeFilter.js';
import { effectiveDashboardHubIds, hubIdsQueryParam } from '../../../utils/dashboardHubFilter.js';
import companySettlementService from '../../../services/companySettlementService.js';
import {
  hasAttendanceAccess,
  hasExternalCommissionAccess,
  hasInternalCommissionDashboardAccess,
  hasOnContractAccess,
  hasOnContractTruckAccess,
} from '../../../utils/networkMode.js';

const canFilterHubs = (role) => role === 'Admin' || role === 'Accountant';
const canFilterCommissionAgent = (role) =>
  role === 'Admin' || role === 'Accountant' || role === 'System Admin' || role === 'Hub Admin';

const VALID_DATE_RANGES = new Set([
  'all_time', 'today', 'this_month', 'last_3_months', 'last_6_months', 'last_year', 'custom',
]);
const DATE_PREFS_VERSION = 2;

const emptyPrefs = {
  dateRange: DEFAULT_DATE_RANGE,
  customStart: '',
  customEnd: '',
  hubIds: [],
  cargoHubIds: [],
  bookingHubIds: [],
  shipmentHubIds: [],
  summaryDate: '',
  bookingMetric: 'net',
  commissionAgentId: '',
  onContractVehicleId: '',
  tripVehicleId: '',
  tripPartyId: '',
  attendanceUserId: '',
  attendanceHubId: '',
  salaryUserId: '',
  salaryHubId: '',
  salaryMonth: '',
  salaryYear: '',
};

const readPrefs = (user) => {
  const cached = readDashboardCache(dashboardCacheKey('ops-prefs', user));
  if (!cached || typeof cached !== 'object') return { ...emptyPrefs };
  let dateRange = VALID_DATE_RANGES.has(cached.dateRange) ? cached.dateRange : DEFAULT_DATE_RANGE;
  const customStart = cached.customStart || '';
  const customEnd = cached.customEnd || '';
  const prefsVersion = Number(cached.datePrefsVersion) || 0;
  if (
    prefsVersion < DATE_PREFS_VERSION
    && dateRange === 'this_month'
    && !customStart
    && !customEnd
  ) {
    dateRange = DEFAULT_DATE_RANGE;
  }
  return {
    ...emptyPrefs,
    ...cached,
    datePrefsVersion: Math.max(prefsVersion, DATE_PREFS_VERSION),
    dateRange: dateRange === 'custom' && (!customStart || !customEnd) ? DEFAULT_DATE_RANGE : dateRange,
    customStart,
    customEnd,
    hubIds: Array.isArray(cached.hubIds) ? cached.hubIds : [],
    cargoHubIds: Array.isArray(cached.cargoHubIds) ? cached.cargoHubIds : [],
    bookingHubIds: Array.isArray(cached.bookingHubIds) ? cached.bookingHubIds : [],
    shipmentHubIds: Array.isArray(cached.shipmentHubIds) ? cached.shipmentHubIds : [],
    bookingMetric: cached.bookingMetric || 'net',
  };
};

const hubsParam = (hubIds) => [...(hubIds || [])].map(String).sort().join(',');

/** Defer below-the-fold dashboard GETs so first paint uses fewer slots. */
function deferSecondaryFetch(run, delayMs = 350) {
  if (typeof window === 'undefined') {
    run();
    return () => {};
  }
  let idleId;
  let timeoutId;
  const start = () => {
    timeoutId = window.setTimeout(run, delayMs);
  };
  if (typeof window.requestIdleCallback === 'function') {
    idleId = window.requestIdleCallback(start, { timeout: delayMs + 400 });
  } else {
    start();
  }
  return () => {
    if (idleId != null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
    if (timeoutId != null) window.clearTimeout(timeoutId);
  };
}

export function useOperationsDashboardData({
  includeUsersVehicles = false,
  includeMlDues = false,
  agentUserIds,
} = {}) {
  const { user } = useContext(AuthContext);
  const agentScopeKey = (() => {
    if (user?.role !== 'Agent') return '';
    const ids = (agentUserIds || []).map(String).filter(Boolean);
    if (!ids.length) return '';
    if (ids.length === 1 && ids[0] === String(user?.user_id || '')) return '';
    return ids.join(',');
  })();
  const agentParams = agentScopeKey ? { agent_user_ids: agentScopeKey } : {};
  const showHubFilter = canFilterHubs(user?.role);
  const showCommissionAgentFilter = canFilterCommissionAgent(user?.role);
  const canFetchOnContracts = hasOnContractAccess(user);
  const canFetchTripContracts = hasOnContractTruckAccess(user);
  const canFetchCommissions = hasInternalCommissionDashboardAccess(user)
    || hasExternalCommissionAccess(user);
  const canFetchAttendance = hasAttendanceAccess(user);
  const canFetchSalaries = hasAttendanceAccess(user);

  const mainKeyFor = (dateRange, customStart, customEnd, hubIds) => dashboardCacheKey('ops-main', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    hubs: hubsParam(hubIds),
    uv: includeUsersVehicles ? 1 : 0,
    ml: includeMlDues ? 1 : 0,
    agents: agentScopeKey,
  });
  const cargoKeyFor = (hubIds) => dashboardCacheKey('ops-cargo', user, {
    hubs: hubsParam(hubIds),
    agents: agentScopeKey,
  });
  const settlementKeyFor = (dateRange, customStart, customEnd, hubIds) => dashboardCacheKey('ops-company-settlement-v2', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    hubs: hubsParam(hubIds),
  });
  const bookingKeyFor = (hubIds, bookingMetric, summaryDate) => dashboardCacheKey('ops-booking', user, {
    hubs: hubsParam(hubIds),
    metric: bookingMetric || 'net',
    date: summaryDate || '',
    agents: agentScopeKey,
  });
  const commissionKeyFor = (dateRange, customStart, customEnd, hubIds, agentId) => dashboardCacheKey('ops-commission', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    hubs: hubsParam(hubIds),
    agent: agentId || '',
    agents: agentScopeKey,
  });
  const onContractKeyFor = (dateRange, customStart, customEnd, vehicleId) => dashboardCacheKey('ops-oncontract', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    vehicle: vehicleId || '',
  });
  const tripKeyFor = (dateRange, customStart, customEnd, vehicleId, partyId) => dashboardCacheKey('ops-trip', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    vehicle: vehicleId || '',
    party: partyId || '',
  });
  const attendanceKeyFor = (dateRange, customStart, customEnd, hubIds, userId, hubId) => dashboardCacheKey('ops-attendance', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    hubs: hubsParam(hubIds),
    user: userId || '',
    hub: hubId || '',
    agents: agentScopeKey,
  });
  const salaryKeyFor = (dateRange, customStart, customEnd, userId, hubId, month, year) => dashboardCacheKey('ops-salary', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    user: userId || '',
    hub: hubId || '',
    month: month || '',
    year: year || '',
    agents: agentScopeKey,
  });

  const initialRef = useRef(null);
  if (initialRef.current == null) {
    const prefs = readPrefs(user);
    const shipmentHubs = effectiveDashboardHubIds(prefs.shipmentHubIds, prefs.hubIds);
    const cargoHubs = effectiveDashboardHubIds(prefs.cargoHubIds, prefs.hubIds);
    const bookingHubs = effectiveDashboardHubIds(prefs.bookingHubIds, prefs.hubIds);
    const main = readDashboardCache(mainKeyFor(prefs.dateRange, prefs.customStart, prefs.customEnd, shipmentHubs));
    const cargo = readDashboardCache(cargoKeyFor(cargoHubs)) || main?.cargoData || null;
    const booking = readDashboardCache(bookingKeyFor(bookingHubs, prefs.bookingMetric, prefs.summaryDate));
    const commission = readDashboardCache(commissionKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.hubIds, prefs.commissionAgentId,
    ));
    const onContract = readDashboardCache(onContractKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.onContractVehicleId,
    ));
    const trip = readDashboardCache(tripKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.tripVehicleId, prefs.tripPartyId,
    ));
    const attendance = readDashboardCache(attendanceKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.hubIds, prefs.attendanceUserId, prefs.attendanceHubId,
    ));
    const salary = readDashboardCache(salaryKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd,
      prefs.salaryUserId, prefs.salaryHubId, prefs.salaryMonth, prefs.salaryYear,
    ));
    const hubs = readDashboardCache(dashboardCacheKey('ops-hubs', user));
    const companySettlement = readDashboardCache(settlementKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.hubIds,
    ));
    initialRef.current = {
      prefs,
      main,
      cargo,
      booking,
      commission,
      companySettlement,
      onContract,
      trip,
      attendance,
      salary,
      hubs,
      hasCache: Boolean(
        main?.shipmentData || cargo || booking || commission
        || onContract || trip || attendance || salary || companySettlement,
      ),
    };
  }
  const initial = initialRef.current;
  const hasInitialCache = initial.hasCache;

  const [loading, setLoading] = useState(!hasInitialCache);
  const [bookingLoading, setBookingLoading] = useState(!initial.booking);
  const [commissionLoading, setCommissionLoading] = useState(!initial.commission);
  const [dateRange, setDateRange] = useState(initial.prefs.dateRange);
  const [customStart, setCustomStart] = useState(initial.prefs.customStart);
  const [customEnd, setCustomEnd] = useState(initial.prefs.customEnd);
  const [hubIds, setHubIdsRaw] = useState(initial.prefs.hubIds);
  const [cargoHubIds, setCargoHubIds] = useState(initial.prefs.cargoHubIds);
  const [bookingHubIds, setBookingHubIds] = useState(initial.prefs.bookingHubIds);
  const [shipmentHubIds, setShipmentHubIds] = useState(initial.prefs.shipmentHubIds);
  const [hubs, setHubs] = useState(Array.isArray(initial.hubs) ? initial.hubs : []);
  const [summaryDate, setSummaryDate] = useState(initial.prefs.summaryDate);
  const [bookingMetric, setBookingMetric] = useState(initial.prefs.bookingMetric);
  const [commissionAgentId, setCommissionAgentId] = useState(initial.prefs.commissionAgentId);
  const [onContractVehicleId, setOnContractVehicleId] = useState(initial.prefs.onContractVehicleId);
  const [tripVehicleId, setTripVehicleId] = useState(initial.prefs.tripVehicleId);
  const [tripPartyId, setTripPartyId] = useState(initial.prefs.tripPartyId);
  const [attendanceUserId, setAttendanceUserId] = useState(initial.prefs.attendanceUserId);
  const [attendanceHubId, setAttendanceHubId] = useState(initial.prefs.attendanceHubId);
  const [salaryUserId, setSalaryUserId] = useState(initial.prefs.salaryUserId);
  const [salaryHubId, setSalaryHubId] = useState(initial.prefs.salaryHubId);
  const [salaryMonth, setSalaryMonth] = useState(initial.prefs.salaryMonth);
  const [salaryYear, setSalaryYear] = useState(initial.prefs.salaryYear);

  const setHubIds = (nextHubIds) => {
    setHubIdsRaw(nextHubIds);
    setCargoHubIds(nextHubIds);
    setBookingHubIds(nextHubIds);
    setShipmentHubIds(nextHubIds);
    setAttendanceHubId('');
    setSalaryHubId('');
  };

  const loadedOnce = useRef(hasInitialCache);
  const [mainRefreshing, setMainRefreshing] = useState(false);

  const [userData, setUserData] = useState(initial.main?.userData || null);
  const [vehicleData, setVehicleData] = useState(initial.main?.vehicleData || null);
  const [shipmentData, setShipmentData] = useState(initial.main?.shipmentData || null);
  const [commissionData, setCommissionData] = useState(initial.commission || null);
  const [onContractData, setOnContractData] = useState(initial.onContract || null);
  const [tripContractData, setTripContractData] = useState(initial.trip || null);
  const [attendanceData, setAttendanceData] = useState(initial.attendance || null);
  const [salaryData, setSalaryData] = useState(initial.salary || null);
  const [mlAgentDues, setMlAgentDues] = useState(initial.main?.mlAgentDues || null);
  const [cargoData, setCargoData] = useState(initial.cargo || initial.main?.cargoData || null);
  const [bookingSummary, setBookingSummary] = useState(initial.booking || null);
  const [cargoLoading, setCargoLoading] = useState(!(initial.cargo || initial.main?.cargoData));
  const [onContractLoading, setOnContractLoading] = useState(!initial.onContract);
  const [tripLoading, setTripLoading] = useState(!initial.trip);
  const [attendanceLoading, setAttendanceLoading] = useState(!initial.attendance);
  const [salaryLoading, setSalaryLoading] = useState(!initial.salary);
  const [companySettlementReview, setCompanySettlementReview] = useState(
    initial.companySettlement || null,
  );
  const [companySettlementLoading, setCompanySettlementLoading] = useState(!initial.companySettlement);

  const showCompanySettlementReview = includeUsersVehicles
    && user?.network_mode !== 'EXTERNAL';

  useEffect(() => {
    writeDashboardCache(dashboardCacheKey('ops-prefs', user), {
      datePrefsVersion: DATE_PREFS_VERSION,
      dateRange, customStart, customEnd, hubIds,
      cargoHubIds, bookingHubIds, shipmentHubIds,
      summaryDate, bookingMetric,
      commissionAgentId, onContractVehicleId, tripVehicleId, tripPartyId,
      attendanceUserId, attendanceHubId, salaryUserId, salaryHubId, salaryMonth, salaryYear,
    });
  }, [
    user?.user_id, user?.company_id, dateRange, customStart, customEnd, hubIds,
    cargoHubIds, bookingHubIds, shipmentHubIds,
    summaryDate, bookingMetric, commissionAgentId, onContractVehicleId, tripVehicleId, tripPartyId,
    attendanceUserId, attendanceHubId, salaryUserId, salaryHubId, salaryMonth, salaryYear,
  ]);

  useEffect(() => {
    if (!showHubFilter) return undefined;
    locationHubService.getHubsForSelect()
      .then((res) => {
        const next = res.data?.hubs || res.data?.data || [];
        setHubs(next);
        writeDashboardCache(dashboardCacheKey('ops-hubs', user), next);
      })
      .catch(() => {});
    return undefined;
  }, [showHubFilter, user?.user_id, user?.company_id]);

  useEffect(() => {
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const shipmentHubs = effectiveDashboardHubIds(shipmentHubIds, hubIds);
    const cacheKey = mainKeyFor(dateRange, customStart, customEnd, shipmentHubs);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      if (cached.shipmentData) setShipmentData(cached.shipmentData);
      if (cached.userData) setUserData(cached.userData);
      if (cached.vehicleData) setVehicleData(cached.vehicleData);
      if (cached.mlAgentDues) setMlAgentDues(cached.mlAgentDues);
      setLoading(false);
      loadedOnce.current = true;
      setMainRefreshing(false);
    } else if (!loadedOnce.current) {
      setLoading(true);
      setMainRefreshing(true);
    } else {
      setShipmentData(null);
      setMainRefreshing(true);
    }
    let cancelled = false;
    const fetchMain = async () => {
      try {
        const hubParams = hubIdsQueryParam(showHubFilter, shipmentHubs);
        const dp = { ...buildDateParams(dateRange, customStart, customEnd), limit: 5, ...hubParams, ...agentParams };
        const requests = [
          dashboardService.getShipments(dp).catch(() => null),
        ];
        if (includeUsersVehicles) {
          requests.push(dashboardService.getUsers().catch(() => null));
          requests.push(dashboardService.getVehicles().catch(() => null));
        }
        if (includeMlDues) {
          requests.push(dashboardService.getMlAgentDues(agentParams).catch(() => null));
        }
        const results = await Promise.all(requests);
        if (cancelled) return;
        const next = { ...(cached || {}) };
        if (results[0]?.data) {
          next.shipmentData = results[0].data;
          setShipmentData(results[0].data);
        }
        let idx = 1;
        if (includeUsersVehicles) {
          if (results[idx]?.data) {
            next.userData = results[idx].data;
            setUserData(results[idx].data);
          }
          if (results[idx + 1]?.data) {
            next.vehicleData = results[idx + 1].data;
            setVehicleData(results[idx + 1].data);
          }
          idx += 2;
        }
        if (includeMlDues && results[idx]?.data) {
          next.mlAgentDues = results[idx].data;
          setMlAgentDues(results[idx].data);
        }
        writeDashboardCache(cacheKey, next);
        loadedOnce.current = true;
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setMainRefreshing(false);
        }
      }
    };
    fetchMain();
    return () => { cancelled = true; };
  }, [
    dateRange, customStart, customEnd, hubIds, shipmentHubIds, showHubFilter,
    includeUsersVehicles, includeMlDues, user?.user_id, user?.company_id, agentScopeKey,
  ]);

  useEffect(() => {
    const cargoHubs = effectiveDashboardHubIds(cargoHubIds, hubIds);
    const cacheKey = cargoKeyFor(cargoHubs);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setCargoData(cached);
      setCargoLoading(false);
    } else {
      setCargoData(null);
      setCargoLoading(true);
    }
    let cancelled = false;
    const fetchCargo = async () => {
      try {
        const hubParams = hubIdsQueryParam(showHubFilter, cargoHubs);
        const res = await dashboardService.getCargoOverview({ ...hubParams, ...agentParams }).catch(() => null);
        if (!cancelled && res?.data) {
          setCargoData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setCargoLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchCargo, 150);
    return () => { cancelled = true; cancelDefer(); };
  }, [hubIds, cargoHubIds, showHubFilter, user?.user_id, user?.company_id, agentScopeKey]);

  useEffect(() => {
    const bookingHubs = effectiveDashboardHubIds(bookingHubIds, hubIds);
    const cacheKey = bookingKeyFor(bookingHubs, bookingMetric, summaryDate);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setBookingSummary(cached);
      setBookingLoading(false);
    } else {
      setBookingSummary(null);
      setBookingLoading(true);
    }
    let cancelled = false;
    const fetchBooking = async () => {
      try {
        const hubParams = hubIdsQueryParam(showHubFilter, bookingHubs);
        const bookingParams = {
          ...hubParams,
          ...agentParams,
          metric: bookingMetric,
          ...(summaryDate ? { summary_date: summaryDate } : {}),
        };
        const res = await dashboardService.getBookingSummary(bookingParams).catch(() => null);
        if (!cancelled && res?.data) {
          setBookingSummary(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setBookingLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchBooking, 250);
    return () => { cancelled = true; cancelDefer(); };
  }, [hubIds, bookingHubIds, showHubFilter, bookingMetric, summaryDate, user?.user_id, user?.company_id, agentScopeKey]);

  useEffect(() => {
    if (!canFetchCommissions) {
      setCommissionData(null);
      setCommissionLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = commissionKeyFor(dateRange, customStart, customEnd, hubIds, commissionAgentId);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setCommissionData(cached);
      setCommissionLoading(false);
    } else {
      setCommissionData(null);
      setCommissionLoading(true);
    }
    let cancelled = false;
    const fetchCommissions = async () => {
      try {
        const hubParams = hubIdsQueryParam(showHubFilter, hubIds);
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 20,
          ...hubParams,
          ...agentParams,
          ...(showCommissionAgentFilter && commissionAgentId
            ? { agent_user_id: commissionAgentId }
            : {}),
        };
        const res = await dashboardService.getCommissions(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setCommissionData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setCommissionLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchCommissions, 200);
    return () => { cancelled = true; cancelDefer(); };
  }, [
    dateRange, customStart, customEnd, hubIds, showHubFilter,
    showCommissionAgentFilter, commissionAgentId, user?.user_id, user?.company_id, agentScopeKey,
    canFetchCommissions,
  ]);

  useEffect(() => {
    if (!canFetchOnContracts) {
      setOnContractData(null);
      setOnContractLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = onContractKeyFor(dateRange, customStart, customEnd, onContractVehicleId);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setOnContractData(cached);
      setOnContractLoading(false);
    } else {
      setOnContractData(null);
      setOnContractLoading(true);
    }
    let cancelled = false;
    const fetchOnContracts = async () => {
      try {
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...(onContractVehicleId ? { vehicle_id: onContractVehicleId } : {}),
        };
        const res = await dashboardService.getOnContracts(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setOnContractData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setOnContractLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchOnContracts, 300);
    return () => { cancelled = true; cancelDefer(); };
  }, [dateRange, customStart, customEnd, onContractVehicleId, user?.user_id, user?.company_id, canFetchOnContracts]);

  useEffect(() => {
    if (!canFetchTripContracts) {
      setTripContractData(null);
      setTripLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = tripKeyFor(dateRange, customStart, customEnd, tripVehicleId, tripPartyId);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setTripContractData(cached);
      setTripLoading(false);
    } else {
      setTripContractData(null);
      setTripLoading(true);
    }
    let cancelled = false;
    const fetchTripContracts = async () => {
      try {
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...(tripVehicleId ? { vehicle_id: tripVehicleId } : {}),
          ...(tripPartyId ? { contract_party_id: tripPartyId } : {}),
        };
        const res = await dashboardService.getTripContracts(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setTripContractData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setTripLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchTripContracts, 450);
    return () => { cancelled = true; cancelDefer(); };
  }, [dateRange, customStart, customEnd, tripVehicleId, tripPartyId, user?.user_id, user?.company_id, canFetchTripContracts]);

  useEffect(() => {
    if (!canFetchAttendance) {
      setAttendanceData(null);
      setAttendanceLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = attendanceKeyFor(
      dateRange, customStart, customEnd, hubIds, attendanceUserId, attendanceHubId,
    );
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setAttendanceData(cached);
      setAttendanceLoading(false);
    } else {
      setAttendanceData(null);
      setAttendanceLoading(true);
    }
    let cancelled = false;
    const fetchAttendance = async () => {
      try {
        const hubParams = hubIdsQueryParam(showHubFilter, hubIds);
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...hubParams,
          ...agentParams,
          ...(attendanceUserId ? { user_id: attendanceUserId } : {}),
          ...(attendanceHubId ? { location_hub_id: attendanceHubId } : {}),
        };
        const res = await dashboardService.getAttendance(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setAttendanceData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setAttendanceLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchAttendance, 600);
    return () => { cancelled = true; cancelDefer(); };
  }, [
    dateRange, customStart, customEnd, hubIds, showHubFilter,
    attendanceUserId, attendanceHubId, user?.user_id, user?.company_id, agentScopeKey,
    canFetchAttendance,
  ]);

  useEffect(() => {
    if (!canFetchSalaries) {
      setSalaryData(null);
      setSalaryLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = salaryKeyFor(
      dateRange, customStart, customEnd, salaryUserId, salaryHubId, salaryMonth, salaryYear,
    );
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setSalaryData(cached);
      setSalaryLoading(false);
    } else {
      setSalaryData(null);
      setSalaryLoading(true);
    }
    let cancelled = false;
    const fetchSalaries = async () => {
      try {
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...agentParams,
          ...(salaryUserId ? { user_id: salaryUserId } : {}),
          ...(salaryHubId ? { location_hub_id: salaryHubId } : {}),
          ...(salaryMonth ? { month: salaryMonth } : {}),
          ...(salaryYear ? { year: salaryYear } : {}),
        };
        const res = await dashboardService.getSalaries(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setSalaryData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setSalaryLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchSalaries, 750);
    return () => { cancelled = true; cancelDefer(); };
  }, [
    dateRange, customStart, customEnd,
    salaryUserId, salaryHubId, salaryMonth, salaryYear, user?.user_id, user?.company_id, agentScopeKey,
    canFetchSalaries,
  ]);

  useEffect(() => {
    if (!showCompanySettlementReview) {
      setCompanySettlementReview(null);
      setCompanySettlementLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = settlementKeyFor(dateRange, customStart, customEnd, hubIds);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      setCompanySettlementReview(cached);
      setCompanySettlementLoading(false);
    } else {
      setCompanySettlementReview(null);
      setCompanySettlementLoading(true);
    }
    let cancelled = false;
    const fetchSettlement = async () => {
      try {
        const hubParams = hubIdsQueryParam(showHubFilter, hubIds);
        const params = {
          ...buildAbsoluteDateParams(dateRange, customStart, customEnd),
          ...hubParams,
        };
        const res = await companySettlementService.getSummary({ ...params, cards: 1 }).catch(() => null);
        const review = res?.data?.review || null;
        if (!cancelled && review) {
          setCompanySettlementReview(review);
          writeDashboardCache(cacheKey, review);
        }
      } finally {
        if (!cancelled) setCompanySettlementLoading(false);
      }
    };
    const cancelDefer = deferSecondaryFetch(fetchSettlement, 500);
    return () => { cancelled = true; cancelDefer(); };
  }, [
    showCompanySettlementReview, dateRange, customStart, customEnd, hubIds, showHubFilter,
    user?.user_id, user?.company_id,
  ]);

  const refreshing = Boolean(
    mainRefreshing || cargoLoading || bookingLoading || commissionLoading || onContractLoading
    || tripLoading || attendanceLoading || salaryLoading
    || companySettlementLoading
    || (loading && hasInitialCache),
  );

  return {
    user,
    loading,
    refreshing,
    mainRefreshing,
    cargoLoading,
    bookingLoading,
    commissionLoading,
    showHubFilter,
    showCommissionAgentFilter,
    dateRange, setDateRange, customStart, setCustomStart, customEnd, setCustomEnd,
    hubIds, setHubIds, hubs,
    cargoHubIds, setCargoHubIds,
    bookingHubIds, setBookingHubIds,
    shipmentHubIds, setShipmentHubIds,
    summaryDate, setSummaryDate, bookingMetric, setBookingMetric,
    commissionAgentId, setCommissionAgentId,
    onContractVehicleId, setOnContractVehicleId,
    tripVehicleId, setTripVehicleId,
    tripPartyId, setTripPartyId,
    attendanceUserId, setAttendanceUserId,
    attendanceHubId, setAttendanceHubId,
    salaryUserId, setSalaryUserId,
    salaryHubId, setSalaryHubId,
    salaryMonth, setSalaryMonth,
    salaryYear, setSalaryYear,
    onContractLoading, tripLoading, attendanceLoading, salaryLoading,
    userData, vehicleData, shipmentData, commissionData,
    onContractData, tripContractData, attendanceData, salaryData,
    mlAgentDues, cargoData, bookingSummary,
    companySettlementReview, companySettlementLoading,
  };
}
